import React, { useMemo, useState } from 'react';
import { Submission, Participant, Task, MediaType } from '../types';
import { UsersIcon, CheckSquareIcon, ClockIcon, BarChartIcon, ChevronDownIcon, XIcon } from './icons';
import Avatar from './Avatar';

/** Convert Firestore Timestamp, ISO string, or Date to a JS Date */
const toDate = (value: any): Date => {
    if (!value) return new Date(0);
    if (typeof value?.toDate === 'function') return value.toDate();
    if (value instanceof Date) return value;
    return new Date(value);
};

// ─── Detail Panel Content Components ───

const ActiveParticipantsDetail: React.FC<{
    participants: Participant[];
    submissions: Submission[];
}> = ({ participants, submissions }) => {
    const participantIdsWithSubmissions = new Set(submissions.map(s => s.participantId));
    const active = participants.filter(p => participantIdsWithSubmissions.has(p.id));
    const inactive = participants.filter(p => !participantIdsWithSubmissions.has(p.id));

    return (
        <div className="space-y-4">
            {/* Active */}
            {active.length > 0 && (
                <div>
                    <p className="text-[11px] font-bold text-status-ok uppercase tracking-widest mb-3">Active ({active.length})</p>
                    <div className="space-y-2">
                        {active.map(p => {
                            const count = submissions.filter(s => s.participantId === p.id).length;
                            return (
                                <div key={p.id} className="flex items-center gap-3">
                                    <Avatar name={p.name} avatarUrl={p.avatarUrl} id={p.id} className="w-7 h-7 rounded-full flex-shrink-0" />
                                    <span className="text-[13px] font-bold text-text-ink flex-1 truncate">{p.name}</span>
                                    <span className="text-[11px] font-medium text-text-muted uppercase tracking-wider">{count} submission{count !== 1 ? 's' : ''}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
            {/* Inactive */}
            {inactive.length > 0 && (
                <div>
                    <p className="text-[11px] font-bold text-text-faint uppercase tracking-wider mb-2">Not Yet Active ({inactive.length})</p>
                    <div className="space-y-2">
                        {inactive.map(p => (
                            <div key={p.id} className="flex items-center gap-3 opacity-60">
                                <Avatar name={p.name} avatarUrl={p.avatarUrl} id={p.id} className="w-7 h-7 rounded-full flex-shrink-0" />
                                <span className="text-[13px] font-medium text-text-ink truncate">{p.name}</span>
                                <span className="text-[11px] text-text-faint uppercase tracking-wider">0 submissions</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const CompletionRateDetail: React.FC<{
    tasks: Task[];
    submissions: Submission[];
    participants: Participant[];
}> = ({ tasks, submissions, participants }) => {
    const taskData = useMemo(() => {
        return tasks.map(task => {
            const assignedIds = task.assignedParticipantIds?.length
                ? task.assignedParticipantIds
                : participants.map(p => p.id);
            const taskSubs = submissions.filter(s => s.taskId === task.id);
            const completedIds = new Set(taskSubs.map(s => s.participantId));
            const completedCount = assignedIds.filter(id => completedIds.has(id)).length;
            const pct = assignedIds.length > 0 ? Math.round((completedCount / assignedIds.length) * 100) : 0;
            return { prompt: task.title || task.prompt, completedCount, total: assignedIds.length, pct };
        }).sort((a, b) => a.pct - b.pct);
    }, [tasks, submissions, participants]);

    return (
        <div className="space-y-3">
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Per-Task Breakdown</p>
            {taskData.map((t, i) => (
                <div key={i}>
                    <div className="flex justify-between items-center mb-1.5">
                        <p className="text-[13px] font-bold text-text-ink truncate max-w-[75%]">{t.prompt}</p>
                        <span className="text-[11px] font-bold text-text-muted uppercase tracking-wider">{t.completedCount}/{t.total}</span>
                    </div>
                    <div className="w-full rounded-full h-1 overflow-hidden" style={{ backgroundColor: '#E8E4DF' }}>
                        <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${t.pct}%`, backgroundColor: t.pct >= 100 ? '#6E7A5A' : t.pct > 0 ? '#7BBFAF' : 'transparent' }}
                        />
                    </div>
                </div>
            ))}
        </div>
    );
};

const PendingReviewsDetail: React.FC<{
    submissions: Submission[];
    participants: Participant[];
}> = ({ submissions, participants }) => {
    const participantMap = new Map<string, Participant>(participants.map(p => [p.id, p]));
    const untagged = submissions.filter(s => !s.tags || s.tags.length === 0);
    const tagged = submissions.length - untagged.length;

    // Group untagged by media type
    const byType = useMemo(() => {
        const groups: Record<string, number> = {};
        untagged.forEach(s => {
            const type = s.mediaType || 'unknown';
            groups[type] = (groups[type] || 0) + 1;
        });
        return groups;
    }, [untagged]);

    const mediaIcons: Record<string, React.ReactNode> = {
        [MediaType.Audio]: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="flex-shrink-0"><rect x="9" y="2" width="6" height="11" rx="3"/><path d="M5 10a7 7 0 0 0 14 0M12 19v3M9 22h6"/></svg>,
        [MediaType.Photo]: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="flex-shrink-0"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21,15 16,10 5,21"/></svg>,
        [MediaType.Video]: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="flex-shrink-0"><polygon points="23,7 16,12 23,17"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>,
        [MediaType.Text]: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="flex-shrink-0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="13" y2="17"/></svg>,
        [MediaType.Screen]: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" className="flex-shrink-0"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
    };
    const mediaLabels: Record<string, string> = {
        [MediaType.Audio]: 'Audio',
        [MediaType.Photo]: 'Photo',
        [MediaType.Video]: 'Video',
        [MediaType.Text]: 'Text',
        [MediaType.Screen]: 'Screen',
    };

    return (
        <div className="space-y-4">
            {/* Summary bar */}
            <div>
                <div className="flex justify-between text-[11px] font-bold text-text-muted uppercase tracking-wider mb-1.5">
                    <span>Reviewed</span>
                    <span>{tagged} of {submissions.length}</span>
                </div>
                <div className="w-full rounded-full h-1 overflow-hidden" style={{ backgroundColor: '#E8E4DF' }}>
                    <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                            width: `${submissions.length > 0 ? (tagged / submissions.length) * 100 : 0}%`,
                            backgroundColor: (() => { const pct = submissions.length > 0 ? (tagged / submissions.length) * 100 : 0; return pct >= 100 ? '#6E7A5A' : pct > 0 ? '#7BBFAF' : 'transparent'; })()
                        }}
                    />
                </div>
            </div>

            {/* By media type */}
            <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2.5">Untagged by Type</p>
                <div className="grid grid-cols-2 gap-2">
                    {Object.entries(byType).map(([type, count]) => (
                        <div key={type} className="bg-app-bg border border-app-border/50 rounded-xl px-3 py-2 flex items-center justify-between">
                            <span className="text-[13px] font-bold text-text-ink flex items-center gap-1.5">{mediaIcons[type]}{mediaLabels[type] || type}</span>
                            <span className="text-[13px] font-bold text-text-muted">{count}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Most recent untagged */}
            {untagged.length > 0 && (
                <div>
                    <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2.5">Recent Untagged</p>
                    <div className="space-y-2">
                        {untagged.slice(0, 4).map(s => {
                            const p = participantMap.get(s.participantId);
                            return (
                                <div key={s.id} className="flex items-center gap-2">
                                    {p && <Avatar name={p.name} avatarUrl={p.avatarUrl} id={p.id} className="w-6 h-6 rounded-full flex-shrink-0" />}
                                    <span className="text-[13px] font-bold text-text-ink truncate flex-1">{p?.name || 'Unknown'}</span>
                                    <span className="text-[11px] font-medium text-text-muted uppercase tracking-wider flex items-center gap-1">{mediaIcons[s.mediaType]}{mediaLabels[s.mediaType] || s.mediaType}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const EngagementDetail: React.FC<{
    submissions: Submission[];
    participants: Participant[];
    tasks: Task[];
}> = ({ submissions, participants, tasks }) => {
    const data = useMemo(() => {
        const ranked = participants.map(p => {
            const count = submissions.filter(s => s.participantId === p.id).length;
            return { participant: p, count };
        }).sort((a, b) => b.count - a.count);

        const avgPerPerson = participants.length > 0 ? submissions.length / participants.length : 0;
        const maxPossible = tasks.length;

        // Submissions over last 7 days
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const recentCount = submissions.filter(s => toDate(s.timestamp) > weekAgo).length;

        return { ranked, avgPerPerson, maxPossible, recentCount };
    }, [submissions, participants, tasks]);

    return (
        <div className="space-y-4">
            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-2">
                <div className="bg-app-bg rounded-xl px-3 py-2.5 text-center">
                    <p className="text-lg font-bold text-chip-photo">{data.avgPerPerson.toFixed(1)}</p>
                    <p className="text-[10px] font-bold text-text-muted uppercase">Avg / Person</p>
                </div>
                <div className="bg-app-bg rounded-xl px-3 py-2.5 text-center">
                    <p className="text-lg font-bold text-chip-photo">{data.recentCount}</p>
                    <p className="text-[10px] font-bold text-text-muted uppercase">Last 7 Days</p>
                </div>
            </div>

            {/* Leaderboard */}
            <div>
                <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-3">Participant Ranking</p>
                <div className="space-y-2">
                    {data.ranked.map((r, i) => (
                        <div key={r.participant.id} className="flex items-center gap-3">
                            <span className="text-xs font-bold text-text-faint w-4">{i + 1}</span>
                            <Avatar name={r.participant.name} avatarUrl={r.participant.avatarUrl} id={r.participant.id} className="w-7 h-7 rounded-full flex-shrink-0" />
                            <span className="text-sm font-semibold text-text-ink flex-1 truncate">{r.participant.name}</span>
                            <div className="flex items-center gap-3">
                                <div className="w-20 rounded-full h-1 overflow-hidden" style={{ backgroundColor: '#E8E4DF' }}>
                                    <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{
                                            width: `${data.maxPossible > 0 ? (r.count / data.maxPossible) * 100 : 0}%`,
                                            backgroundColor: (() => { const pct = data.maxPossible > 0 ? (r.count / data.maxPossible) * 100 : 0; return pct >= 100 ? '#6E7A5A' : pct > 0 ? '#7BBFAF' : 'transparent'; })()
                                        }}
                                    />
                                </div>
                                <span className="text-[11px] font-bold text-text-muted w-6 text-right">{r.count}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── Main Components ───

interface StatCardProps {
    icon: React.ReactNode;
    title: string;
    value: string | number;
    subtitle?: string;
    bgClass: string;
    textClass: string;
    isExpanded: boolean;
    onToggle: () => void;
    detailContent: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ icon, title, value, subtitle, bgClass, textClass, isExpanded, onToggle, detailContent }) => (
    <div className={`rounded-xl border border-app-border shadow-sm transition-all duration-300 bg-white ${!isExpanded ? 'hover:-translate-y-0.5' : ''}`}>
        <button
            onClick={onToggle}
            className="w-full text-left p-4 sm:p-5 flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-start gap-4 cursor-pointer focus:outline-none"
        >
            <div className={`rounded-xl p-2.5 ${bgClass} ${textClass} flex-shrink-0`}>
                {icon}
            </div>

            <div className="flex flex-col items-start flex-1 min-w-0">
                <p className="text-[10px] sm:text-[11px] font-bold text-text-muted uppercase tracking-wider mb-0.5">{title}</p>
                <div className="flex items-baseline gap-2 sm:block">
                    <p className="text-xl sm:text-2xl font-bold text-text-ink">{value}</p>
                    {subtitle && <p className="text-[10px] sm:text-[11px] font-medium text-text-muted sm:mt-1 truncate">{subtitle}</p>}
                </div>
            </div>

            <div className={`p-1.5 rounded-full transition-all duration-300 ${isExpanded ? 'bg-action/10 rotate-180' : 'bg-transparent hover:bg-action/5'}`}>
                <ChevronDownIcon className="w-4 h-4 text-text-muted" />
            </div>
        </button>

        {/* Expandable Detail Panel */}
        <div
            className={`overflow-hidden transition-all duration-400 ease-in-out ${isExpanded ? 'max-h-[380px] opacity-100' : 'max-h-0 opacity-0'}`}
        >
            <div className="px-5 sm:px-6 pb-4 border-t border-app-border pt-4 h-[320px] overflow-y-auto custom-scrollbar">
                {detailContent}
            </div>
        </div>
    </div>
);

interface DashboardStatsRowProps {
    submissions: Submission[];
    participants: Participant[];
    tasks: Task[];
}

const DashboardStatsRow: React.FC<DashboardStatsRowProps> = ({ submissions, participants, tasks }) => {
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

    const toggle = (card: string) => {
        setExpandedCards(prev => {
            const next = new Set(prev);
            if (next.has(card)) next.delete(card);
            else next.add(card);
            return next;
        });
    };

    const stats = useMemo(() => {
        const participantIdsWithSubmissions = new Set(submissions.map(s => s.participantId));
        const activeParticipants = participants.filter(p => participantIdsWithSubmissions.has(p.id)).length;

        const totalExpected = tasks.length * participants.length;
        const completionRate = totalExpected > 0
            ? Math.round((submissions.length / totalExpected) * 100)
            : 0;

        const pendingReviews = submissions.filter(s => !s.tags || s.tags.length === 0).length;

        const avgSubmissions = activeParticipants > 0
            ? submissions.length / activeParticipants
            : 0;
        let engagement: string;
        if (avgSubmissions >= 3) engagement = 'High';
        else if (avgSubmissions >= 1) engagement = 'Medium';
        else engagement = 'Low';

        return { activeParticipants, completionRate, pendingReviews, engagement };
    }, [submissions, participants, tasks]);

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <StatCard
                icon={<UsersIcon className="w-6 h-6" />}
                title="Active Participants"
                value={stats.activeParticipants}
                subtitle={`of ${participants.length} total`}
                bgClass="bg-action/10"
                textClass="text-action"
                isExpanded={expandedCards.has('participants')}
                onToggle={() => toggle('participants')}
                detailContent={<ActiveParticipantsDetail participants={participants} submissions={submissions} />}
            />
            <StatCard
                icon={<CheckSquareIcon className="w-6 h-6" />}
                title="Completion Rate"
                value={`${stats.completionRate}%`}
                subtitle={`${submissions.length} submissions`}
                bgClass="bg-status-ok-bg"
                textClass="text-status-ok"
                isExpanded={expandedCards.has('completion')}
                onToggle={() => toggle('completion')}
                detailContent={<CompletionRateDetail tasks={tasks} submissions={submissions} participants={participants} />}
            />
            <StatCard
                icon={<ClockIcon className="w-6 h-6" />}
                title="Pending Reviews"
                value={stats.pendingReviews}
                subtitle="untagged submissions"
                bgClass="bg-action/10"
                textClass="text-action"
                isExpanded={expandedCards.has('pending')}
                onToggle={() => toggle('pending')}
                detailContent={<PendingReviewsDetail submissions={submissions} participants={participants} />}
            />
            <StatCard
                icon={<BarChartIcon className="w-6 h-6" />}
                title="Engagement"
                value={stats.engagement}
                subtitle={`avg ${(submissions.length / Math.max(participants.length, 1)).toFixed(1)} per person`}
                bgClass="bg-chip-photo/10"
                textClass="text-chip-photo"
                isExpanded={expandedCards.has('engagement')}
                onToggle={() => toggle('engagement')}
                detailContent={<EngagementDetail submissions={submissions} participants={participants} tasks={tasks} />}
            />
        </div>
    );
};

export default DashboardStatsRow;
